import ErrorPage from '@apps-common/ui-components/ErrorPage.jsx';
import {ErrorBoundary} from '@sentry/react';
import PropTypes from 'prop-types';
import React, {Suspense, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import BasemapsSidebar from './basemaps/index.jsx';
import {LeftDrawer, RightDrawer} from './drawers.jsx';
import LeftDrawerHeader from './left-drawer-header.jsx';
import SearchSidebar from './search/index.jsx';
import {showLeftDrawer, showRightDrawer} from '@explorer/actions/ui';
import {LoadingMessage} from '@explorer/components/ui/message-container';
import {DRAWERS} from '@explorer/constants';
import lazyRetry from '@explorer/util/lazyRetry';

const Folders = lazyRetry(
  () => import(/* webpackChunkName: "sidebar-folders" */ './folders/index.jsx')
);
const ColorEnhancementSidebar = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "sidebar-color-enhancement" */ './color-enhancement/index.jsx'
    )
);
const OrdersSidebar = lazyRetry(
  () => /* webpackChunkName: "sidebar-orders" */ import('./orders/index.jsx')
);
const ProcessChooserSidebar = lazyRetry(
  () =>
    /* webpackChunkName: "sidebar-process-chooser" */ import(
      './process-chooser/index.jsx'
    )
);
const SavedSearchesSidebar = lazyRetry(
  () =>
    /* webpackChunkName: "sidebar-saved-searches" */ import(
      './saved-searches/index.jsx'
    )
);
const SettingsSidebar = lazyRetry(
  () =>
    /* webpackChunkName: "sidebar-settings" */ import('./settings/index.jsx')
);

const CollectionsSidebar = lazyRetry(
  () =>
    /* webpackChunkName: "sidebar-collections" */ import(
      './feature-collections/index.jsx'
    )
);

const StatusDrawer = ({hasError, side}) => {
  const drawerList = {
    left: LeftDrawer,
    right: RightDrawer,
  };

  const SideDrawer = drawerList[side];

  /* istanbul ignore next */
  if (hasError) {
    return (
      <SideDrawer open>
        <ErrorPage />
      </SideDrawer>
    );
  }

  return (
    <SideDrawer open>
      {side === 'left' && <LeftDrawerHeader />}
      <LoadingMessage />
    </SideDrawer>
  );
};

StatusDrawer.propTypes = {
  hasError: PropTypes.bool,
  side: PropTypes.oneOf(['left', 'right']).isRequired,
};

const Sidebars = ({
  openDrawers: {left, right},
  showLeftDrawer,
  showRightDrawer,
}) => {
  const [componentsToLoad, setComponentsToLoad] = useState([]);

  const lazyloadLeftSideComponentsMap = {
    [DRAWERS.FOLDERS]: Folders,
    [DRAWERS.ORDERS]: OrdersSidebar,
    [DRAWERS.SAVED_SEARCHES]: SavedSearchesSidebar,
    [DRAWERS.SETTINGS]: SettingsSidebar,
    [DRAWERS.COLLECTIONS]: CollectionsSidebar,
  };

  const lazyloadRightSideComponentsMap = {
    [DRAWERS.COLORENHANCE]: ColorEnhancementSidebar,
    [DRAWERS.PROCESS]: ProcessChooserSidebar,
  };

  useEffect(() => {
    [left, right].forEach(side => {
      if (Boolean(side) && !componentsToLoad.includes(side)) {
        setComponentsToLoad(prevState => [...prevState, side]);
      }
    });
  }, [componentsToLoad, left, right]);

  return (
    <React.Fragment>
      <SearchSidebar open={left === DRAWERS.SEARCH} />
      <BasemapsSidebar open={left === DRAWERS.BASEMAPS} />

      {componentsToLoad.map(component => {
        const SidebarComponent =
          lazyloadLeftSideComponentsMap[component] ||
          lazyloadRightSideComponentsMap[component];

        return (
          <React.Fragment key={component}>
            {Boolean(SidebarComponent) && (
              <ErrorBoundary
                fallback={
                  <StatusDrawer
                    hasError
                    side={left === component ? 'left' : 'right'}
                  />
                }
              >
                <Suspense
                  fallback={
                    <StatusDrawer
                      side={left === component ? 'left' : 'right'}
                    />
                  }
                >
                  <SidebarComponent
                    onClose={
                      left === component
                        ? () => showLeftDrawer('')
                        : () => showRightDrawer('')
                    }
                    open={left === component || right === component}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

Sidebars.propTypes = {
  openDrawers: PropTypes.shape({
    left: PropTypes.string,
    right: PropTypes.string,
  }).isRequired,
  showLeftDrawer: PropTypes.func.isRequired,
  showRightDrawer: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  openDrawers: state.ui.drawers,
});

const mapDispatchProps = {
  showLeftDrawer,
  showRightDrawer,
};

export default connect(mapStateToProps, mapDispatchProps)(Sidebars);
